/**
 * Air Quality Sensor Calculations
 *
 * Comprehensive utility for air quality monitoring including:
 * - CO2 (Carbon Dioxide) monitoring
 * - VOC (Volatile Organic Compounds) detection
 * - PM2.5 (Particulate Matter) measurement
 * - AQI (Air Quality Index) calculation
 * - Health impact assessment
 * - Ventilation recommendations
 */

// ==================== Types ====================

export type AirQualityLevel =
  | 'excellent'
  | 'good'
  | 'moderate'
  | 'poor'
  | 'very_poor'
  | 'hazardous';

export type CO2Level = 'excellent' | 'good' | 'acceptable' | 'poor' | 'very_poor';

export type VOCLevel = 'excellent' | 'good' | 'moderate' | 'unhealthy' | 'very_unhealthy';

export type PM25Level =
  | 'good'
  | 'moderate'
  | 'unhealthy_sensitive'
  | 'unhealthy'
  | 'very_unhealthy'
  | 'hazardous';

export interface AirQualityReading {
  timestamp: Date;
  co2?: number; // ppm (parts per million)
  voc?: number; // ppb (parts per billion)
  pm25?: number; // μg/m³ (micrograms per cubic meter)
  aqi?: number; // Air Quality Index (0-500)
  temperature?: number; // °C
  humidity?: number; // %
}

export interface AirQualityStats {
  averageCO2: number;
  averageVOC: number;
  averagePM25: number;
  averageAQI: number;
  maxCO2: number;
  maxVOC: number;
  maxPM25: number;
  maxAQI: number;
  minCO2: number;
  minVOC: number;
  minPM25: number;
  minAQI: number;
  readingCount: number;
  excellentHours: number;
  goodHours: number;
  moderateHours: number;
  poorHours: number;
  hazardousHours: number;
  ventilationNeeded: boolean;
  healthRisk: 'low' | 'moderate' | 'high' | 'very_high';
}

export interface AirQualityHourPattern {
  hour: number;
  averageCO2: number;
  averageVOC: number;
  averagePM25: number;
  averageAQI: number;
  readingCount: number;
  airQualityLevel: AirQualityLevel;
  isPeakPollution: boolean;
}

export interface AirQualityAlert {
  timestamp: Date;
  type: 'co2_high' | 'voc_high' | 'pm25_high' | 'aqi_poor' | 'ventilation_needed';
  severity: 'warning' | 'danger' | 'critical';
  message: string;
  value: number;
  threshold: number;
  recommendation: string;
}

export interface AirQualityStatus {
  currentCO2: number;
  currentVOC: number;
  currentPM25: number;
  currentAQI: number;
  overallLevel: AirQualityLevel;
  co2Level: CO2Level;
  vocLevel: VOCLevel;
  pm25Level: PM25Level;
  healthRisk: 'low' | 'moderate' | 'high' | 'very_high';
  ventilationNeeded: boolean;
  recommendations: string[];
  alerts: AirQualityAlert[];
  todayStats: AirQualityStats;
  yesterdayStats: AirQualityStats;
  weekStats: AirQualityStats;
  monthStats: AirQualityStats;
  trend: 'improving' | 'stable' | 'worsening';
}

// ==================== Core Functions ====================

/**
 * Calculate CO2 level classification
 */
export function getCO2Level(co2: number): CO2Level {
  if (co2 < 400) return 'excellent';
  if (co2 < 600) return 'good';
  if (co2 < 1000) return 'acceptable';
  if (co2 < 1500) return 'poor';
  return 'very_poor';
}

/**
 * Calculate VOC level classification
 */
export function getVOCLevel(voc: number): VOCLevel {
  if (voc < 220) return 'excellent';
  if (voc < 660) return 'good';
  if (voc < 2200) return 'moderate';
  if (voc < 5500) return 'unhealthy';
  return 'very_unhealthy';
}

/**
 * Calculate PM2.5 level classification (EPA standard)
 */
export function getPM25Level(pm25: number): PM25Level {
  if (pm25 <= 12) return 'good';
  if (pm25 <= 35.4) return 'moderate';
  if (pm25 <= 55.4) return 'unhealthy_sensitive';
  if (pm25 <= 150.4) return 'unhealthy';
  if (pm25 <= 250.4) return 'very_unhealthy';
  return 'hazardous';
}

/**
 * Calculate Air Quality Index from PM2.5
 * Using EPA breakpoints
 */
export function calculateAQIFromPM25(pm25: number): number {
  const breakpoints = [
    { cLow: 0, cHigh: 12, iLow: 0, iHigh: 50 },
    { cLow: 12.1, cHigh: 35.4, iLow: 51, iHigh: 100 },
    { cLow: 35.5, cHigh: 55.4, iLow: 101, iHigh: 150 },
    { cLow: 55.5, cHigh: 150.4, iLow: 151, iHigh: 200 },
    { cLow: 150.5, cHigh: 250.4, iLow: 201, iHigh: 300 },
    { cLow: 250.5, cHigh: 500, iLow: 301, iHigh: 500 },
  ];

  for (const bp of breakpoints) {
    if (pm25 >= bp.cLow && pm25 <= bp.cHigh) {
      const aqi = ((bp.iHigh - bp.iLow) / (bp.cHigh - bp.cLow)) * (pm25 - bp.cLow) + bp.iLow;
      return Math.round(aqi);
    }
  }

  return pm25 > 500 ? 500 : 0;
}

/**
 * Get overall air quality level from AQI
 */
export function getAQILevel(aqi: number): AirQualityLevel {
  if (aqi <= 50) return 'excellent';
  if (aqi <= 100) return 'good';
  if (aqi <= 150) return 'moderate';
  if (aqi <= 200) return 'poor';
  if (aqi <= 300) return 'very_poor';
  return 'hazardous';
}

/**
 * Calculate average values from readings
 */
export function calculateAverages(
  readings: AirQualityReading[],
  startDate: Date,
  endDate: Date,
): { avgCO2: number; avgVOC: number; avgPM25: number; avgAQI: number } {
  const filtered = readings.filter((r) => r.timestamp >= startDate && r.timestamp <= endDate);

  if (filtered.length === 0) {
    return { avgCO2: 0, avgVOC: 0, avgPM25: 0, avgAQI: 0 };
  }

  const co2Values = filtered.filter((r) => r.co2 !== undefined).map((r) => r.co2!);
  const vocValues = filtered.filter((r) => r.voc !== undefined).map((r) => r.voc!);
  const pm25Values = filtered.filter((r) => r.pm25 !== undefined).map((r) => r.pm25!);
  const aqiValues = filtered.filter((r) => r.aqi !== undefined).map((r) => r.aqi!);

  const avgCO2 =
    co2Values.length > 0 ? co2Values.reduce((sum, v) => sum + v, 0) / co2Values.length : 0;

  const avgVOC =
    vocValues.length > 0 ? vocValues.reduce((sum, v) => sum + v, 0) / vocValues.length : 0;

  const avgPM25 =
    pm25Values.length > 0 ? pm25Values.reduce((sum, v) => sum + v, 0) / pm25Values.length : 0;

  const avgAQI =
    aqiValues.length > 0 ? aqiValues.reduce((sum, v) => sum + v, 0) / aqiValues.length : 0;

  return { avgCO2, avgVOC, avgPM25, avgAQI };
}

/**
 * Calculate comprehensive air quality statistics
 */
export function getAirQualityStatistics(
  readings: AirQualityReading[],
  startDate: Date,
  endDate: Date,
): AirQualityStats {
  const filtered = readings.filter((r) => r.timestamp >= startDate && r.timestamp <= endDate);

  if (filtered.length === 0) {
    return {
      averageCO2: 0,
      averageVOC: 0,
      averagePM25: 0,
      averageAQI: 0,
      maxCO2: 0,
      maxVOC: 0,
      maxPM25: 0,
      maxAQI: 0,
      minCO2: 0,
      minVOC: 0,
      minPM25: 0,
      minAQI: 0,
      readingCount: 0,
      excellentHours: 0,
      goodHours: 0,
      moderateHours: 0,
      poorHours: 0,
      hazardousHours: 0,
      ventilationNeeded: false,
      healthRisk: 'low',
    };
  }

  const co2Values = filtered.filter((r) => r.co2 !== undefined).map((r) => r.co2!);
  const vocValues = filtered.filter((r) => r.voc !== undefined).map((r) => r.voc!);
  const pm25Values = filtered.filter((r) => r.pm25 !== undefined).map((r) => r.pm25!);
  const aqiValues = filtered.filter((r) => r.aqi !== undefined).map((r) => r.aqi!);

  const averageCO2 =
    co2Values.length > 0 ? co2Values.reduce((sum, v) => sum + v, 0) / co2Values.length : 0;
  const averageVOC =
    vocValues.length > 0 ? vocValues.reduce((sum, v) => sum + v, 0) / vocValues.length : 0;
  const averagePM25 =
    pm25Values.length > 0 ? pm25Values.reduce((sum, v) => sum + v, 0) / pm25Values.length : 0;
  const averageAQI =
    aqiValues.length > 0 ? aqiValues.reduce((sum, v) => sum + v, 0) / aqiValues.length : 0;

  // Calculate quality hours
  let excellentHours = 0;
  let goodHours = 0;
  let moderateHours = 0;
  let poorHours = 0;
  let hazardousHours = 0;

  filtered.forEach((reading) => {
    const aqi = reading.aqi || (reading.pm25 ? calculateAQIFromPM25(reading.pm25) : 0);
    const level = getAQILevel(aqi);

    if (level === 'excellent') excellentHours++;
    else if (level === 'good') goodHours++;
    else if (level === 'moderate') moderateHours++;
    else if (level === 'poor' || level === 'very_poor') poorHours++;
    else if (level === 'hazardous') hazardousHours++;
  });

  const ventilationNeeded = averageCO2 > 1000 || averageVOC > 660 || averagePM25 > 35;

  let healthRisk: 'low' | 'moderate' | 'high' | 'very_high' = 'low';
  if (averageAQI > 200 || averageCO2 > 1500) healthRisk = 'very_high';
  else if (averageAQI > 150 || averageCO2 > 1000) healthRisk = 'high';
  else if (averageAQI > 100 || averageCO2 > 800) healthRisk = 'moderate';

  return {
    averageCO2: Math.round(averageCO2),
    averageVOC: Math.round(averageVOC),
    averagePM25: Math.round(averagePM25 * 10) / 10,
    averageAQI: Math.round(averageAQI),
    maxCO2: co2Values.length > 0 ? Math.max(...co2Values) : 0,
    maxVOC: vocValues.length > 0 ? Math.max(...vocValues) : 0,
    maxPM25: pm25Values.length > 0 ? Math.max(...pm25Values) : 0,
    maxAQI: aqiValues.length > 0 ? Math.max(...aqiValues) : 0,
    minCO2: co2Values.length > 0 ? Math.min(...co2Values) : 0,
    minVOC: vocValues.length > 0 ? Math.min(...vocValues) : 0,
    minPM25: pm25Values.length > 0 ? Math.min(...pm25Values) : 0,
    minAQI: aqiValues.length > 0 ? Math.min(...aqiValues) : 0,
    readingCount: filtered.length,
    excellentHours: Math.round(excellentHours / 60), // Convert to hours
    goodHours: Math.round(goodHours / 60),
    moderateHours: Math.round(moderateHours / 60),
    poorHours: Math.round(poorHours / 60),
    hazardousHours: Math.round(hazardousHours / 60),
    ventilationNeeded,
    healthRisk,
  };
}

/**
 * Get 24-hour air quality pattern
 */
export function getAirQualityPattern(
  readings: AirQualityReading[],
  date: Date,
): AirQualityHourPattern[] {
  const pattern: AirQualityHourPattern[] = [];
  const dateStart = new Date(date);
  dateStart.setHours(0, 0, 0, 0);

  for (let hour = 0; hour < 24; hour++) {
    const hourStart = new Date(dateStart);
    hourStart.setHours(hour);
    const hourEnd = new Date(hourStart);
    hourEnd.setHours(hour + 1);

    const hourReadings = readings.filter((r) => r.timestamp >= hourStart && r.timestamp < hourEnd);

    if (hourReadings.length > 0) {
      const { avgCO2, avgVOC, avgPM25, avgAQI } = calculateAverages(
        hourReadings,
        hourStart,
        hourEnd,
      );

      pattern.push({
        hour,
        averageCO2: Math.round(avgCO2),
        averageVOC: Math.round(avgVOC),
        averagePM25: Math.round(avgPM25 * 10) / 10,
        averageAQI: Math.round(avgAQI),
        readingCount: hourReadings.length,
        airQualityLevel: getAQILevel(avgAQI),
        isPeakPollution: false,
      });
    } else {
      pattern.push({
        hour,
        averageCO2: 0,
        averageVOC: 0,
        averagePM25: 0,
        averageAQI: 0,
        readingCount: 0,
        airQualityLevel: 'excellent',
        isPeakPollution: false,
      });
    }
  }

  // Mark peak pollution hour
  if (pattern.length > 0) {
    const maxAQI = Math.max(...pattern.map((p) => p.averageAQI));
    const peakIndex = pattern.findIndex((p) => p.averageAQI === maxAQI);
    if (peakIndex >= 0) {
      pattern[peakIndex].isPeakPollution = true;
    }
  }

  return pattern;
}

/**
 * Generate health recommendations based on air quality
 */
export function generateRecommendations(
  co2: number,
  voc: number,
  pm25: number,
  aqi: number,
  language: 'en' | 'ar' = 'en',
): string[] {
  const recommendations: string[] = [];

  if (language === 'ar') {
    // Arabic recommendations
    if (co2 > 1500) {
      recommendations.push('افتح النوافذ فوراً - مستوى CO2 خطير');
    } else if (co2 > 1000) {
      recommendations.push('قم بتهوية المكان - مستوى CO2 مرتفع');
    } else if (co2 > 800) {
      recommendations.push('يُنصح بالتهوية الدورية');
    }

    if (voc > 2200) {
      recommendations.push('تجنب استخدام المواد الكيميائية');
    } else if (voc > 660) {
      recommendations.push('قلل مصادر المركبات العضوية');
    }

    if (pm25 > 150) {
      recommendations.push('ابقَ في الداخل - جودة هواء خطيرة');
      recommendations.push('استخدم جهاز تنقية الهواء');
    } else if (pm25 > 55) {
      recommendations.push('قلل النشاط الخارجي');
      recommendations.push('أغلق النوافذ');
    } else if (pm25 > 35) {
      recommendations.push('يُنصح الأشخاص الحساسون بالحذر');
    }

    if (aqi > 200) {
      recommendations.push('تحذير صحي - ارتدِ كمامة N95');
    } else if (aqi > 150) {
      recommendations.push('جودة هواء غير صحية');
    } else if (aqi > 100) {
      recommendations.push('جودة هواء مقبولة للأشخاص الأصحاء');
    } else if (aqi <= 50) {
      recommendations.push('جودة هواء ممتازة - استمتع بالأنشطة الخارجية');
    }
  } else {
    // English recommendations
    if (co2 > 1500) {
      recommendations.push('Open windows immediately - dangerous CO2 level');
    } else if (co2 > 1000) {
      recommendations.push('Ventilate the space - high CO2 level');
    } else if (co2 > 800) {
      recommendations.push('Periodic ventilation recommended');
    }

    if (voc > 2200) {
      recommendations.push('Avoid using chemicals');
    } else if (voc > 660) {
      recommendations.push('Reduce VOC sources');
    }

    if (pm25 > 150) {
      recommendations.push('Stay indoors - hazardous air quality');
      recommendations.push('Use air purifier');
    } else if (pm25 > 55) {
      recommendations.push('Reduce outdoor activities');
      recommendations.push('Close windows');
    } else if (pm25 > 35) {
      recommendations.push('Sensitive groups should be cautious');
    }

    if (aqi > 200) {
      recommendations.push('Health warning - wear N95 mask');
    } else if (aqi > 150) {
      recommendations.push('Unhealthy air quality');
    } else if (aqi > 100) {
      recommendations.push('Acceptable for healthy individuals');
    } else if (aqi <= 50) {
      recommendations.push('Excellent air quality - enjoy outdoor activities');
    }
  }

  return recommendations.length > 0
    ? recommendations
    : language === 'ar'
    ? ['جودة الهواء جيدة']
    : ['Air quality is good'];
}

/**
 * Generate air quality alerts
 */
export function generateAlerts(
  readings: AirQualityReading[],
  recentHours: number = 1,
): AirQualityAlert[] {
  const alerts: AirQualityAlert[] = [];
  const cutoff = new Date(Date.now() - recentHours * 60 * 60 * 1000);
  const recent = readings.filter((r) => r.timestamp >= cutoff);

  if (recent.length === 0) return alerts;

  const latest = recent[recent.length - 1];

  // CO2 alerts
  if (latest.co2 && latest.co2 > 1500) {
    alerts.push({
      timestamp: latest.timestamp,
      type: 'co2_high',
      severity: 'critical',
      message: 'Dangerous CO2 level detected',
      value: latest.co2,
      threshold: 1500,
      recommendation: 'Open windows immediately and ensure proper ventilation',
    });
  } else if (latest.co2 && latest.co2 > 1000) {
    alerts.push({
      timestamp: latest.timestamp,
      type: 'co2_high',
      severity: 'danger',
      message: 'High CO2 level',
      value: latest.co2,
      threshold: 1000,
      recommendation: 'Increase ventilation',
    });
  }

  // VOC alerts
  if (latest.voc && latest.voc > 2200) {
    alerts.push({
      timestamp: latest.timestamp,
      type: 'voc_high',
      severity: 'danger',
      message: 'Unhealthy VOC level',
      value: latest.voc,
      threshold: 2200,
      recommendation: 'Identify and remove VOC sources',
    });
  } else if (latest.voc && latest.voc > 660) {
    alerts.push({
      timestamp: latest.timestamp,
      type: 'voc_high',
      severity: 'warning',
      message: 'Elevated VOC level',
      value: latest.voc,
      threshold: 660,
      recommendation: 'Reduce use of chemicals and fragrances',
    });
  }

  // PM2.5 alerts
  if (latest.pm25 && latest.pm25 > 150) {
    alerts.push({
      timestamp: latest.timestamp,
      type: 'pm25_high',
      severity: 'critical',
      message: 'Hazardous particulate matter',
      value: latest.pm25,
      threshold: 150,
      recommendation: 'Stay indoors and use air purifier',
    });
  } else if (latest.pm25 && latest.pm25 > 55) {
    alerts.push({
      timestamp: latest.timestamp,
      type: 'pm25_high',
      severity: 'danger',
      message: 'Unhealthy PM2.5 level',
      value: latest.pm25,
      threshold: 55,
      recommendation: 'Limit outdoor exposure',
    });
  }

  // AQI alerts
  const aqi = latest.aqi || (latest.pm25 ? calculateAQIFromPM25(latest.pm25) : 0);
  if (aqi > 200) {
    alerts.push({
      timestamp: latest.timestamp,
      type: 'aqi_poor',
      severity: 'critical',
      message: 'Very unhealthy air quality',
      value: aqi,
      threshold: 200,
      recommendation: 'Health alert - everyone should avoid outdoor activities',
    });
  } else if (aqi > 150) {
    alerts.push({
      timestamp: latest.timestamp,
      type: 'aqi_poor',
      severity: 'danger',
      message: 'Unhealthy air quality',
      value: aqi,
      threshold: 150,
      recommendation: 'Sensitive groups should stay indoors',
    });
  }

  // Ventilation needed
  if (latest.co2 && latest.co2 > 1000) {
    alerts.push({
      timestamp: latest.timestamp,
      type: 'ventilation_needed',
      severity: 'warning',
      message: 'Ventilation recommended',
      value: latest.co2,
      threshold: 1000,
      recommendation: 'Open windows or increase air circulation',
    });
  }

  return alerts;
}

/**
 * Get comprehensive air quality status
 */
export function getAirQualityStatus(
  readings: AirQualityReading[],
  currentCO2: number = 400,
  currentVOC: number = 0,
  currentPM25: number = 0,
): AirQualityStatus {
  const now = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const yesterdayEnd = new Date(todayStart);

  const weekStart = new Date(now);
  weekStart.setDate(weekStart.getDate() - 7);

  const monthStart = new Date(now);
  monthStart.setMonth(monthStart.getMonth() - 1);

  const currentAQI = calculateAQIFromPM25(currentPM25);
  const overallLevel = getAQILevel(currentAQI);
  const co2Level = getCO2Level(currentCO2);
  const vocLevel = getVOCLevel(currentVOC);
  const pm25Level = getPM25Level(currentPM25);

  let healthRisk: 'low' | 'moderate' | 'high' | 'very_high' = 'low';
  if (currentAQI > 200 || currentCO2 > 1500) healthRisk = 'very_high';
  else if (currentAQI > 150 || currentCO2 > 1000) healthRisk = 'high';
  else if (currentAQI > 100 || currentCO2 > 800) healthRisk = 'moderate';

  const ventilationNeeded = currentCO2 > 1000 || currentVOC > 660 || currentPM25 > 35;

  const recommendations = generateRecommendations(currentCO2, currentVOC, currentPM25, currentAQI);
  const alerts = generateAlerts(readings, 1);

  const todayStats = getAirQualityStatistics(readings, todayStart, now);
  const yesterdayStats = getAirQualityStatistics(readings, yesterdayStart, yesterdayEnd);
  const weekStats = getAirQualityStatistics(readings, weekStart, now);
  const monthStats = getAirQualityStatistics(readings, monthStart, now);

  // Determine trend
  let trend: 'improving' | 'stable' | 'worsening' = 'stable';
  if (todayStats.averageAQI < yesterdayStats.averageAQI - 10) {
    trend = 'improving';
  } else if (todayStats.averageAQI > yesterdayStats.averageAQI + 10) {
    trend = 'worsening';
  }

  return {
    currentCO2: Math.round(currentCO2),
    currentVOC: Math.round(currentVOC),
    currentPM25: Math.round(currentPM25 * 10) / 10,
    currentAQI: Math.round(currentAQI),
    overallLevel,
    co2Level,
    vocLevel,
    pm25Level,
    healthRisk,
    ventilationNeeded,
    recommendations,
    alerts,
    todayStats,
    yesterdayStats,
    weekStats,
    monthStats,
    trend,
  };
}

// ==================== Formatting Functions ====================

/**
 * Format CO2 value with unit
 */
export function formatCO2(co2: number, language: 'en' | 'ar' = 'en'): string {
  if (language === 'ar') {
    return `${co2.toLocaleString('ar-EG')} جزء/مليون`;
  }
  return `${co2} ppm`;
}

/**
 * Format VOC value with unit
 */
export function formatVOC(voc: number, language: 'en' | 'ar' = 'en'): string {
  if (language === 'ar') {
    return `${voc.toLocaleString('ar-EG')} جزء/بليون`;
  }
  return `${voc} ppb`;
}

/**
 * Format PM2.5 value with unit
 */
export function formatPM25(pm25: number, language: 'en' | 'ar' = 'en'): string {
  const rounded = Math.round(pm25 * 10) / 10;
  if (language === 'ar') {
    return `${rounded.toLocaleString('ar-EG')} μg/m³`;
  }
  return `${rounded} μg/m³`;
}

/**
 * Format AQI value
 */
export function formatAQI(aqi: number, language: 'en' | 'ar' = 'en'): string {
  if (language === 'ar') {
    return aqi.toLocaleString('ar-EG');
  }
  return aqi.toString();
}

/**
 * Format CO2 level text
 */
export function formatCO2Level(level: CO2Level, language: 'en' | 'ar' = 'en'): string {
  if (language === 'ar') {
    const levels = {
      excellent: 'ممتاز',
      good: 'جيد',
      acceptable: 'مقبول',
      poor: 'ضعيف',
      very_poor: 'سيء جداً',
    };
    return levels[level];
  }

  const levels = {
    excellent: 'Excellent',
    good: 'Good',
    acceptable: 'Acceptable',
    poor: 'Poor',
    very_poor: 'Very Poor',
  };
  return levels[level];
}

/**
 * Format VOC level text
 */
export function formatVOCLevel(level: VOCLevel, language: 'en' | 'ar' = 'en'): string {
  if (language === 'ar') {
    const levels = {
      excellent: 'ممتاز',
      good: 'جيد',
      moderate: 'متوسط',
      unhealthy: 'غير صحي',
      very_unhealthy: 'غير صحي جداً',
    };
    return levels[level];
  }

  const levels = {
    excellent: 'Excellent',
    good: 'Good',
    moderate: 'Moderate',
    unhealthy: 'Unhealthy',
    very_unhealthy: 'Very Unhealthy',
  };
  return levels[level];
}

/**
 * Format PM2.5 level text
 */
export function formatPM25Level(level: PM25Level, language: 'en' | 'ar' = 'en'): string {
  if (language === 'ar') {
    const levels = {
      good: 'جيد',
      moderate: 'متوسط',
      unhealthy_sensitive: 'غير صحي للحساسين',
      unhealthy: 'غير صحي',
      very_unhealthy: 'غير صحي جداً',
      hazardous: 'خطير',
    };
    return levels[level];
  }

  const levels = {
    good: 'Good',
    moderate: 'Moderate',
    unhealthy_sensitive: 'Unhealthy for Sensitive',
    unhealthy: 'Unhealthy',
    very_unhealthy: 'Very Unhealthy',
    hazardous: 'Hazardous',
  };
  return levels[level];
}

/**
 * Format AQI level text
 */
export function formatAQILevel(level: AirQualityLevel, language: 'en' | 'ar' = 'en'): string {
  if (language === 'ar') {
    const levels = {
      excellent: 'ممتاز',
      good: 'جيد',
      moderate: 'متوسط',
      poor: 'ضعيف',
      very_poor: 'سيء جداً',
      hazardous: 'خطير',
    };
    return levels[level];
  }

  const levels = {
    excellent: 'Excellent',
    good: 'Good',
    moderate: 'Moderate',
    poor: 'Poor',
    very_poor: 'Very Poor',
    hazardous: 'Hazardous',
  };
  return levels[level];
}

/**
 * Format health risk level
 */
export function formatHealthRisk(
  risk: 'low' | 'moderate' | 'high' | 'very_high',
  language: 'en' | 'ar' = 'en',
): string {
  if (language === 'ar') {
    const risks = {
      low: 'منخفض',
      moderate: 'متوسط',
      high: 'مرتفع',
      very_high: 'مرتفع جداً',
    };
    return risks[risk];
  }

  const risks = {
    low: 'Low',
    moderate: 'Moderate',
    high: 'High',
    very_high: 'Very High',
  };
  return risks[risk];
}

/**
 * Format trend text
 */
export function formatTrend(
  trend: 'improving' | 'stable' | 'worsening',
  language: 'en' | 'ar' = 'en',
): string {
  if (language === 'ar') {
    const trends = {
      improving: 'يتحسّن',
      stable: 'مستقر',
      worsening: 'يتدهور',
    };
    return trends[trend];
  }

  const trends = {
    improving: 'Improving',
    stable: 'Stable',
    worsening: 'Worsening',
  };
  return trends[trend];
}

/**
 * Get AQI color
 */
export function getAQIColor(aqi: number): string {
  if (aqi <= 50) return '#10b981'; // Green - Excellent
  if (aqi <= 100) return '#84cc16'; // Lime - Good
  if (aqi <= 150) return '#f59e0b'; // Orange - Moderate
  if (aqi <= 200) return '#ef4444'; // Red - Poor
  if (aqi <= 300) return '#dc2626'; // Dark Red - Very Poor
  return '#7f1d1d'; // Darkest Red - Hazardous
}

/**
 * Get CO2 level color
 */
export function getCO2LevelColor(level: CO2Level): string {
  const colors = {
    excellent: '#10b981',
    good: '#84cc16',
    acceptable: '#f59e0b',
    poor: '#ef4444',
    very_poor: '#dc2626',
  };
  return colors[level];
}

/**
 * Get VOC level color
 */
export function getVOCLevelColor(level: VOCLevel): string {
  const colors = {
    excellent: '#10b981',
    good: '#84cc16',
    moderate: '#f59e0b',
    unhealthy: '#ef4444',
    very_unhealthy: '#dc2626',
  };
  return colors[level];
}

/**
 * Get PM2.5 level color
 */
export function getPM25LevelColor(level: PM25Level): string {
  const colors = {
    good: '#10b981',
    moderate: '#84cc16',
    unhealthy_sensitive: '#f59e0b',
    unhealthy: '#ef4444',
    very_unhealthy: '#dc2626',
    hazardous: '#7f1d1d',
  };
  return colors[level];
}
